import {
  Address,
  DraftEmailType,
  MessagingPlatform,
  UnifiedSendEmailResponse,
} from '@this+that/models';
import log from 'loglevel';

import { DraftConflictResult } from './draftConflict';
import { buildDraftRequest } from './draftRequest';
import {
  PAYLOAD_TOO_LARGE_RESULT,
  PayloadTooLargeResult,
} from './payloadTooLarge';
import { DraftAccount, resolveDraftAccount } from './resolveEmailAccount';
import {
  DraftVersionConflictError,
  RequestPayloadTooLargeError,
} from '../../../services/unifiedEmailAction.service';
import { useIntegrationsStore } from '../../../store';
import { sendDraftWrite } from '../../../utils/draftWrites';
import type { FileAttachment } from '../../index';

export type SavedDraftResult = UnifiedSendEmailResponse & {
  platform: MessagingPlatform;
  draftAccount?: DraftAccount;
};

export const useSaveDraft = () => {
  const { currentIntegrationAccount, integrationAccounts } =
    useIntegrationsStore();

  const saveDraft = async (
    content?: string,
    subject?: string,
    recipients: Address[] = [],
    attachments?: FileAttachment[],
    draftId?: string,
    fromEmail?: string,
    messageId?: string,
    draftType?: DraftEmailType,
    modifiedQuotedContent?: string,
    expectedDraftVersion?: string,
    pinnedAccount?: DraftAccount,
    attachmentsToRemove?: string[],
    lineageId?: string,
  ): Promise<
    SavedDraftResult | DraftConflictResult | PayloadTooLargeResult | null
  > => {
    try {
      if (!currentIntegrationAccount) {
        log.error('No integration mail found');
        return null;
      }

      const draftAccount =
        draftId && pinnedAccount
          ? pinnedAccount
          : resolveDraftAccount(
              fromEmail,
              currentIntegrationAccount,
              integrationAccounts,
            );

      if (!draftAccount) {
        return null;
      }

      const { platform } = draftAccount;

      const payload = buildDraftRequest(
        {
          content,
          subject,
          recipients,
          attachments,
          draftId,
          messageId,
          draftType,
          modifiedQuotedContent,
          expectedDraftVersion,
          attachmentsToRemove,
        },
        draftAccount,
      );

      const response = await sendDraftWrite(payload, { lineageId });
      return {
        ...(response.message as UnifiedSendEmailResponse),
        platform,
        draftAccount,
      };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        log.debug('Save draft request was aborted');
        return null;
      }
      if (error instanceof DraftVersionConflictError) {
        log.warn('Draft version conflict while saving:', error);
        return {
          isDraftConflict: true,
          currentDraftVersion: error.currentDraftVersion,
        };
      }
      if (error instanceof RequestPayloadTooLargeError) {
        log.warn('Draft too large to save:', error);
        return PAYLOAD_TOO_LARGE_RESULT;
      }
      log.warn('Failed to save draft silently:', error);
      return null;
    }
  };

  return {
    saveDraft,
  };
};
